import { create } from 'zustand';
import {
  deriveKeys,
  generatePIN,
  generateSalt,
  saveCredentials,
  loadCredentials,
  clearCredentials,
} from '../crypto';
import type { FileManifest } from '../crypto/chunked';

export interface ChunkInfo {
  index: number;
  total: number;
  fileId: string;
}

export interface Message {
  id: string;
  type: 'text' | 'image' | 'image_start' | 'image_chunk' | 'image_complete' | 'video' | 'file_chunk' | 'file_manifest';
  content?: string;           // For text
  data?: string;              // For image data / chunk data
  mimeType?: string;          // image/jpeg, image/png, etc.
  filename?: string;          // Encrypted filename
  size?: number;              // File size
  sender: string;
  timestamp: number;

  // New chunked transfer fields (for image_start, image_chunk, image_complete)
  fileId?: string;            // Unique file ID for chunked transfer
  totalSize?: number;         // Total file size (image_start)
  totalChunks?: number;       // Total number of chunks (image_start)
  index?: number;             // Current chunk index (image_chunk)

  // Legacy fields (for backward compatibility)
  chunkInfo?: ChunkInfo;      // Legacy: For old file_chunk type
  manifest?: FileManifest;    // Legacy: For old file_manifest type
}

interface ClipState {
  // Identity
  uuid: string | null;
  pin: string | null;
  salt: string | null;
  isCreator: boolean;
  createdAt: number | null; // Clip creation timestamp for PIN visibility

  // Security
  cryptoKey: CryptoKey | null;
  authToken: string | null;

  // Connection
  connected: boolean;
  connecting: boolean;
  peerCount: number;
  error: string | null;
  ws: WebSocket | null; // Global WebSocket instance

  // Data
  messages: Message[];
  mySenderId: string | null;

  // Actions
  createClip: () => Promise<{ uuid: string; pin: string; url: string }>;
  joinClip: (uuid: string, pin: string, salt: string, isCreator?: boolean) => Promise<boolean>;
  setCryptoKey: (key: CryptoKey) => void;
  setAuthToken: (token: string) => void;
  setPin: (pin: string | null) => void;
  setIsCreator: (isCreator: boolean) => void;
  setConnected: (connected: boolean) => void;
  setConnecting: (connecting: boolean) => void;
  setPeerCount: (count: number) => void;
  setError: (error: string | null) => void;
  setWs: (ws: WebSocket | null) => void;
  addMessage: (message: Message) => void;
  setMessages: (messages: Message[]) => void;
  setMySenderId: (id: string) => void;
  clearError: () => void;
  reset: () => void;
  // Try auto-join with stored credentials
  tryAutoJoin: (uuid: string) => Promise<{ success: boolean; pin?: string; salt?: string; isCreator?: boolean }>;
  // Clear stored credentials
  clearStoredCredentials: (uuid?: string) => Promise<void>;
}

const API_URL = import.meta.env.VITE_API_URL || '';

export const useClipStore = create<ClipState>((set) => ({
  // Initial state
  uuid: null,
  pin: null,
  salt: null,
  isCreator: false,
  createdAt: null,
  cryptoKey: null,
  authToken: null,
  connected: false,
  connecting: false,
  peerCount: 0,
  error: null,
  ws: null,
  messages: [],
  mySenderId: null,

  // Create new clip
  createClip: async () => {
    const pin = generatePIN();
    const salt = generateSalt();

    // Step 1: Create on server (get UUID)
    const response = await fetch(`${API_URL}/api/clips`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ salt }),
    });

    if (!response.ok) {
      throw new Error('Failed to create clip');
    }

    const { uuid } = await response.json();

    // Step 2: Derive keys with actual UUID
    const { authToken: realAuthToken, cryptoKey: realCryptoKey, authHash } = await deriveKeys(uuid, pin, salt);

    // Step 3: Setup authHash on server
    const setupResponse = await fetch(`${API_URL}/api/clips/${uuid}/setup`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ authHash }),
    });

    if (!setupResponse.ok) {
      throw new Error('Failed to setup clip');
    }

    set({
      uuid,
      pin,
      salt,
      isCreator: true,
      createdAt: Date.now(),
      cryptoKey: realCryptoKey,
      authToken: realAuthToken,
      messages: [],
    });

    // Save credentials to secure local storage
    try {
      await saveCredentials(uuid, pin, salt, true);
    } catch (e) {
      console.warn('Failed to save credentials locally:', e);
      // Don't fail the operation if local storage fails
    }

    return {
      uuid,
      pin,
      url: `${window.location.origin}/c/${uuid}`,
    };
  },

  // Join existing clip
  joinClip: async (uuid: string, pin: string, salt: string, isCreator = false) => {
    try {
      const { authToken, cryptoKey } = await deriveKeys(uuid, pin, salt);

      set({
        uuid,
        pin,
        salt,
        isCreator,
        cryptoKey,
        authToken,
        messages: [],
      });

      // Save credentials to secure local storage
      try {
        await saveCredentials(uuid, pin, salt, isCreator);
      } catch (e) {
        console.warn('Failed to save credentials locally:', e);
        // Don't fail the operation if local storage fails
      }

      return true;
    } catch (e) {
      set({ error: 'Failed to derive keys' });
      return false;
    }
  },

  setCryptoKey: (key) => set({ cryptoKey: key }),
  setAuthToken: (token) => set({ authToken: token }),
  setPin: (pin) => set({ pin }),
  setIsCreator: (isCreator) => set({ isCreator }),
  setConnected: (connected) => set({ connected }),
  setConnecting: (connecting) => set({ connecting }),
  setPeerCount: (count) => set({ peerCount: count }),
  setError: (error) => set({ error }),
  setWs: (ws) => set({ ws }),
  addMessage: (message) => set((state) => ({ messages: [...state.messages, message] })),
  setMessages: (messages) => set({ messages }),
  setMySenderId: (id) => set({ mySenderId: id }),
  clearError: () => set({ error: null }),

  reset: () =>
    set({
      uuid: null,
      pin: null,
      salt: null,
      isCreator: false,
      createdAt: null,
      cryptoKey: null,
      authToken: null,
      connected: false,
      connecting: false,
      peerCount: 0,
      error: null,
      messages: [],
      mySenderId: null,
      ws: null,
    }),

  // Try to auto-join using stored credentials
  tryAutoJoin: async (uuid: string) => {
    try {
      const stored = await loadCredentials(uuid);
      if (stored) {
        return { success: true, pin: stored.pin, salt: stored.salt, isCreator: stored.isCreator };
      }
      return { success: false };
    } catch (e) {
      console.warn('Failed to load stored credentials:', e);
      return { success: false };
    }
  },

  // Clear stored credentials
  clearStoredCredentials: async (uuid?: string) => {
    try {
      if (uuid) {
        await clearCredentials(uuid);
      } else {
        // Clear credentials for current clip if any
        const state = useClipStore.getState();
        if (state.uuid) {
          await clearCredentials(state.uuid);
        }
      }
    } catch (e) {
      console.warn('Failed to clear stored credentials:', e);
    }
  },
}));
